// Types and small helpers for the Studio workbench (mirror
// apps/api/src/services/studio/workbench/* and routes/studio-workbench.ts).

export type RunStatus = "queued" | "running" | "passed" | "failed" | "error" | "accepted" | "rejected";
export interface Run {
  id: string; listingId: string; step: "photo" | "description"; engine: string;
  params: { colour?: string; view?: string; backdrop?: string; label?: string; sourceUrl?: string };
  status: RunStatus;
  result: { pass?: boolean; reasons?: string[]; checks?: Record<string, unknown>; text?: string | null; throttled?: string; acceptedText?: string };
  outputUrl: string | null; costUsd: number | null; seconds: number | null; jobId: string | null; comparisonId: string | null;
  createdAt: string; finishedAt: string | null;
}
export interface EngineCard { id: string; label: string; blurb: string; kind?: string; costUsd: number; seconds: number; ready: boolean; missing: string[] }
export interface KeyStatus { name: string; label: string; configured: boolean; usedFor: string; howToAdd: string }
export interface PhotoDefault { engine: string; backdrop: string; views: string[] }
export interface EngineSettings { photos: { fallback: PhotoDefault; byDepartment: Record<string, PhotoDefault> }; description: { engine: string } }
export interface Backdrop { name: string; label: string; colour: [number, number, number] }
export interface EnginesInfo {
  engines: { photo: EngineCard[]; text: EngineCard[] }; keys: KeyStatus[]; settings: EngineSettings; updatedAt: string | null; stored: boolean;
  backdrops: Backdrop[]; views: string[]; departments: Array<{ letter: string; name: string }>;
  scoreboard: Array<{ step: string; department: string | null; engine: string; wins: number }>;
  worker: WorkerStatus;
}
export interface WorkerStatus { inflight: number; throttle: { provider: string; message: string; at: string } | null }
export interface ColourViews { colour: string; views: Array<{ view: string; sourceUrl: string; label: string; original: string }> }
export interface WorkbenchProduct {
  id: string; name: string; sku: string | null; brand: string | null; supplier: string | null; supplierStyle: string | null; ready: boolean;
  department: { letter: string; name: string } | null;
  colours: ColourViews[];
  approved: Array<{ id: string; colour: string; view: string; sourceUrl: string; studioUrl: string; recipe: string }>;
  runs: Run[];
  nameStep: { current: string; supplier: string | null; proposed: string; fixes: string[] };
  descriptionStep: { current: string | null; source: string; supplier: string | null };
  skuStep: { listing: string | null; variants: number; variantsMissing: number; canAssign: boolean; why: string | null; schemaReady: boolean };
  categoryStep: { current: { slug: string; name: string } | null; setByHand: boolean; suggestion: { slug: string; name: string; confidence: string | null; decidedBy: string | null } | null; suspicion: string | null };
  defaults: { photo: PhotoDefault; description: string };
  workbenchReady: boolean;
  worker: WorkerStatus;
}
export interface Estimate { photos: number; descriptions: number; costUsd: number; minutes: number; byEngine: Record<string, { count: number; costUsd: number }> }
export interface Job {
  id: string; label: string; status: "queued" | "running" | "done" | "failed" | "cancelled"; total: number; done: number; failed: number; planned: boolean;
  costEstimateUsd: number; createdAt: string; finishedAt: string | null; listingIds: string[]; steps: { photos?: { engine: string } | null; description?: { engine: string } | null };
}

export const VIEW_LABEL: Record<string, string> = { front: "Front", back: "Back", model: "On model", model_back: "On model, back", side: "Side", detail: "Close-up" };

export const money = (usd: number) => (usd < 1 ? `${Math.max(1, Math.round(usd * 100))}¢` : `$${usd.toFixed(2)}`);
export const duration = (s: number) => (s < 90 ? `${Math.round(s)} s` : `${Math.round(s / 60)} min`);
export const newKey = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
export const hex = (c: readonly number[]) => `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;

/** Plain words for a run's state. */
export function statusWords(r: Run): { text: string; tone: "ok" | "warn" | "risk" | "muted" } {
  switch (r.status) {
    case "queued": return { text: r.result.throttled ? "Waiting — provider is throttling" : "Waiting", tone: "muted" };
    case "running": return { text: "Working…", tone: "muted" };
    case "passed": return { text: "Passed checks", tone: "ok" };
    case "failed": return { text: "Failed checks", tone: "risk" };
    case "error": return { text: "Didn't finish", tone: "risk" };
    case "accepted": return { text: "Accepted — live", tone: "ok" };
    case "rejected": return { text: "Rejected", tone: "muted" };
  }
}
export const isOpen = (r: Run) => r.status === "queued" || r.status === "running";

/** The check numbers worth showing beside a photo, in plain words. */
export function photoScores(r: Run): string[] {
  const c = r.result.checks ?? {};
  const out: string[] = [];
  if (typeof c.pixelDeMean === "number") out.push(`product pixels ΔE ${c.pixelDeMean.toFixed(2)}`);
  const ai = c.ai as { colourLock?: { deBefore: number; deAfter: number }; judge?: { look_quality?: number; house_consistency?: number; differences?: string } | null } | undefined;
  if (ai?.colourLock) out.push(`colour ΔE ${ai.colourLock.deBefore.toFixed(1)} → ${ai.colourLock.deAfter.toFixed(1)}`);
  if (ai?.judge?.look_quality) out.push(`look ${ai.judge.look_quality}/5`);
  if (ai?.judge?.house_consistency) out.push(`house style ${ai.judge.house_consistency}/5`);
  return out;
}
